// src/hooks/useChatWebSocket.ts

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  ChatMessage,
  ChatSource,
  WebSocketMessage,
  MessageSource,
  MessageFeedback,
} from '../types/chat';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

// --- IMPORTANT: Get User ID and WS URL ---
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const WS_URL_BASE = import.meta.env.VITE_WS_URL_BASE || 'ws://localhost:8000/ws/chat/';

const generateSimpleId = (): string => uuidv4();
// -----------------------------------------

export const useChatWebSocket = (
  initialMessagesProp: ChatMessage[] = [],
  onSessionCreated?: (sessionId: string) => void
) => {
  const [token, setToken] = useState<string | null>(null);
  const [sessionIdFromUrl, setSessionIdFromUrl] = useState<string | null>(null);
  const [currentWsSessionId, setCurrentWsSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessagesProp);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);
  const [historyLoaded, setHistoryLoaded] = useState<boolean>(false);

  // Keep locale for potential future use
  const [locale, setLocale] = useState<string | null>(null);

  const ws = useRef<WebSocket | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const forceReconnect = useRef<boolean>(false);
  const justUpdatedUrl = useRef<boolean>(false);
  const isConnectingOrConnected = useRef<boolean>(false);
  const lastConnectedSessionId = useRef<string | null>(null);
  const tokenRef = useRef<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const historyLoadedRef = useRef<boolean>(false);
  const currentWsSessionIdRef = useRef<string | null>(null);
  const messagesRef = useRef<ChatMessage[]>(messages);

  const historyLoadPromise = useRef<Promise<void> | null>(null);
  const historyLoadAttempts = useRef(0);
  const wsConnectionFailed = useRef<boolean>(false);
  const connectionAttempts = useRef<number>(0);
  // const initialMessageProcessed = useRef<boolean>(false); // Removed unused variable

  const [isTyping, setIsTyping] = useState(false);

  const [_source, setSource] = useState<ChatSource>(ChatSource.DEFAULT);

  // === NEW: helper to upsert messages by id (dedupe + merge) ===
  const upsertMessage = useCallback((incoming: ChatMessage) => {
    setMessages(prev => {
      if (incoming.id) {
        const idx = prev.findIndex(m => m.id === incoming.id);
        if (idx !== -1) {
          const merged = { ...prev[idx], ...incoming };
          const copy = [...prev];
          copy[idx] = merged;
          return copy;
        }
      }
      return [...prev, incoming];
    });
  }, []);

  // Update refs when values change
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    sessionIdRef.current = sessionIdFromUrl;
  }, [sessionIdFromUrl]);

  useEffect(() => {
    historyLoadedRef.current = historyLoaded;
  }, [historyLoaded]);

  useEffect(() => {
    currentWsSessionIdRef.current = currentWsSessionId;
  }, [currentWsSessionId]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Extract URL params
  // Extract URL params with useMemo to prevent infinite loops
  const urlParams = useMemo(
    () => ({
      rawToken: searchParams.get('token') || '',
      rawSessionId: searchParams.get('sessionId') || '',
      rawLocale: searchParams.get('locale') || '',
      rawUserQuery: searchParams.get('userQuery') || '',
      rawQuestion: searchParams.get('Question') || '',
      rawSource: searchParams.get('source') || ChatSource.DEFAULT,
      newSessionParam: searchParams.get('newSession'),
    }),
    [searchParams]
  );

  const {
    rawToken,
    rawSessionId,
    rawLocale,
    rawUserQuery,
    rawQuestion,
    rawSource,
    newSessionParam,
  } = urlParams;

  // Update URL with a new session ID without reload
  const updateUrlWithSessionId = useCallback(
    (sessionId: string) => {
      const params = new URLSearchParams(searchParams);
      params.set('sessionId', sessionId);
      params.delete('newSession');
      params.delete('refresh');

      const newUrl = `?${params.toString()}`;

      try {
        navigate(newUrl, { replace: true });
      } catch (_error) {
        try {
          const fullUrl = `${window.location.pathname}${newUrl}`;
          window.history.replaceState({}, '', fullUrl);
        } catch {
          // Ignore fallback errors
          return;
        }
      }

      justUpdatedUrl.current = true;

      setTimeout(() => {
        if (justUpdatedUrl.current) {
          justUpdatedUrl.current = false;
        }
      }, 100);
    },
    [searchParams, navigate]
  );

  const loadHistoryOnce = useCallback(
    async (sessionId: string, token: string): Promise<boolean> => {
      if (historyLoadPromise.current) {
        await historyLoadPromise.current;
        return messagesRef.current.length > 0;
      }

      historyLoadPromise.current = (async () => {
        setIsLoadingHistory(true);

        // Don't retry if WebSocket connection failed - it indicates a backend issue
        let retries = wsConnectionFailed.current ? 1 : 3;
        while (retries > 0) {
          try {
            const controller = new AbortController();
            setTimeout(() => controller.abort(), 8000);

            const response = await fetch(
              `${API_BASE_URL}/chat/messages/${sessionId}/${token}/?t=${Date.now()}`,
              { signal: controller.signal }
            );

            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();

            if (Array.isArray(data)) {
              setMessages(data);
              setHistoryLoaded(true);
              historyLoadAttempts.current = 0;
              break;
            }
          } catch (err: unknown) {
            retries--;
            historyLoadAttempts.current++;

            const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';

            if (retries === 0) {
              setError(`Failed to load history: ${errorMessage}`);
            } else if (!wsConnectionFailed.current) {
              // Only retry if WebSocket connection is healthy
              await new Promise(resolve => setTimeout(resolve, 1000));
            }
          }
        }

        setIsLoadingHistory(false);
        historyLoadPromise.current = null;
      })();

      await historyLoadPromise.current;
      return messagesRef.current.length > 0;
    },
    []
  );

  // Fetch chat history from API
  const getPreviousChats = useCallback(async () => {
    if (!rawSessionId || !rawToken) return;
    await loadHistoryOnce(rawSessionId, rawToken);
  }, [rawSessionId, rawToken, loadHistoryOnce]);

  // React to URL param changes
  useEffect(() => {
    if (justUpdatedUrl.current) {
      justUpdatedUrl.current = false;
      return;
    }

    // Reset connection state when URL params change (new session/token)
    wsConnectionFailed.current = false;
    connectionAttempts.current = 0;

    setToken(rawToken);
    setLocale(rawLocale || null);

    const parsedSource = Object.values(ChatSource).includes(rawSource as ChatSource)
      ? (rawSource as ChatSource)
      : ChatSource.DEFAULT;
    setSource(parsedSource);

    if (rawSessionId) {
      if (sessionIdFromUrl !== rawSessionId) {
        setMessages([]);
        setHistoryLoaded(false);
        setSessionIdFromUrl(rawSessionId);

        if (lastConnectedSessionId.current !== rawSessionId) {
          // Load history only once when switching to a new session
          getPreviousChats().then(() => {
            // Mark that we've loaded history via REST API
            // console.log(""); // Removed empty console.log
          });

          forceReconnect.current = true;
        }
      } else {
        // console.log(`Session ID from URL (${rawSessionId}) matches current, no action needed.`); // Removed empty block
      }
    } else {
      setSessionIdFromUrl('');
      setCurrentWsSessionId(null);
      setMessages([]);
      setHistoryLoaded(true);
      forceReconnect.current = true;
    }
  }, [rawToken, rawSessionId, rawLocale, newSessionParam, getPreviousChats, sessionIdFromUrl]);

  const handleSessionIdUpdate = useCallback(
    (sessionId: string) => {
      const currentSessionFromUrl = sessionIdRef.current;
      const currentWsId = currentWsSessionIdRef.current;

      if (currentWsId !== sessionId) {
        setCurrentWsSessionId(sessionId);

        // Always update URL if we don't have a session ID from URL
        if (!currentSessionFromUrl) {
          setSessionIdFromUrl(sessionId);
          updateUrlWithSessionId(sessionId);

          if (onSessionCreated) {
            onSessionCreated(sessionId);
          }

          // console.log(`New session created: ${sessionId}, URL updated.`); // Removed empty console.log
        }
      }
    },
    [updateUrlWithSessionId, onSessionCreated]
  );

  const handleStreamingMessage = (msg: WebSocketMessage) => {
    // Remove any existing typing placeholder before adding streaming chunks
    setMessages(prev => prev.filter(m => !m.isTypingPlaceholder));

    const tempId = `streaming-${msg.session_id || 'no-session'}`;
    if (msg.chunk_type === 'partial') {
      setMessages(prev => {
        const idx = prev.findIndex(m => m.id === tempId);
        if (idx !== -1) {
          const copy = [...prev];
          copy[idx] = { ...copy[idx], text: (copy[idx].text || '') + (msg.message || '') };
          return copy;
        }
        return [
          ...prev,
          {
            id: tempId,
            role: 'ai',
            text: msg.message || '',
            timestamp: msg.timestamp ?? Date.now(),
            session_id: msg.session_id,
            isStreaming: true, // Mark as streaming
          },
        ];
      });
    } else if (msg.chunk_type === 'complete') {
      // Replace temp with final id and ensure isStreaming is false for complete message
      setMessages(prev => {
        const idx = prev.findIndex(m => m.id === tempId);
        if (idx === -1) return prev;
        const finalId = msg.id || generateSimpleId();
        const copy = [...prev];
        copy[idx] = {
          ...copy[idx],
          id: finalId,
          isStreaming: false,
          sources: msg.sources || [],
        }; // Turn off streaming flag
        return copy;
      });
    }
  };

  // Process conversation history from WebSocket
  const handleConversationHistory = useCallback(
    (historyData: {
      messages?: Array<{
        id?: string;
        type: string;
        message: string;
        timestamp?: number;
        sources?: MessageSource[];
        feedback?: MessageFeedback;
      }>;
      session_id: string;
    }) => {
      if (historyData.messages && Array.isArray(historyData.messages)) {
        const convertedMessages: ChatMessage[] = historyData.messages.map(msg => ({
          id: msg.id || generateSimpleId(),
          role: (msg.type === 'user' ? 'user' : 'ai') as ChatMessage['role'],
          text: msg.message,
          timestamp: msg.timestamp || Date.now(),
          session_id: historyData.session_id,
          sources: msg.sources || [],
          feedback: msg.feedback,
        }));

        setMessages(convertedMessages);
        setHistoryLoaded(true);

        return true;
      }

      return false;
    },
    []
  );

  // WebSocket connector
  const connectWebSocket = useCallback(() => {
    const currentToken = tokenRef.current;
    const currentSessionId = sessionIdRef.current;

    if (isConnectingOrConnected.current && !forceReconnect.current) {
      return;
    }
    if (!currentToken) {
      if (!error) setError('Authentication token required.');
      return;
    }

    // cleanup any old WS
    if (ws.current) {
      ws.current.onopen = ws.current.onmessage = ws.current.onclose = ws.current.onerror = null;
      try {
        ws.current.close(1000, 'Reconnecting');
      } catch {
        // Ignore errors during cleanup
      }
      ws.current = null;
    }

    isConnectingOrConnected.current = true;
    forceReconnect.current = false;
    wsConnectionFailed.current = false; // Reset connection failure flag
    connectionAttempts.current += 1; // Track connection attempts
    lastConnectedSessionId.current = currentSessionId;

    // Build WebSocket URL with all URL params as query string
    const segment = currentSessionId || '';
    const params = new URLSearchParams();
    if (rawLocale) params.set('locale', rawLocale);
    if (rawUserQuery) params.set('userQuery', rawUserQuery);
    if (rawQuestion) params.set('Question', rawQuestion);
    if (rawSource && rawSource !== ChatSource.DEFAULT) params.set('source', rawSource);

    const queryString = params.toString();
    const url = `${WS_URL_BASE}${currentToken}/${segment}${queryString ? '?' + queryString : ''}`;

    setError(null);
    ws.current = new WebSocket(url);

    ws.current.onopen = () => {
      setIsConnected(true);

      // Only clear messages when starting a brand new session
      if (!currentSessionId) {
        setMessages([]);
      }

      // ===== CRITICAL FIX: Only pre-seed messages for NEW sessions =====
      // Check if this is a resumed session by looking at current messages
      const isResumingSession = currentSessionId && messagesRef.current.length > 0;

      if (!isResumingSession) {
        // Handle Dashboard scenario - always has both Question and userQuery
        if (
          !currentSessionId &&
          rawSource === ChatSource.DASHBOARD &&
          rawQuestion &&
          rawUserQuery
        ) {
          const systemQuestionDecoded = decodeURIComponent(rawQuestion);
          const userQueryDecoded = decodeURIComponent(rawUserQuery);

          // Add the dashboard system question as a system-type message for UI display
          upsertMessage({
            id: `dashboard-question-${Date.now()}`,
            role: 'system',
            text: systemQuestionDecoded,
            timestamp: Date.now(),
            session_id: undefined,
            isDashboardQuestion: true, // Special flag to style it differently
          });

          // Add the user query as user message
          upsertMessage({
            id: generateSimpleId(),
            role: 'user',
            text: userQueryDecoded,
            timestamp: Date.now(),
            session_id: undefined,
          });

          // Add typing indicator for AI response
          setMessages(prev => [
            ...prev,
            {
              id: `typing-placeholder-${Date.now()}`,
              role: 'ai',
              text: null,
              timestamp: Date.now(),
              isTypingPlaceholder: true,
              session_id: undefined,
            },
          ]);
          setIsTyping(true);
        }
        // === Existing: Pre-seed user message when userQuery is present for DEFAULT scenarios ===
        else if (
          !currentSessionId &&
          rawUserQuery &&
          (!rawSource || rawSource === ChatSource.DEFAULT)
        ) {
          // Pre-seed user message only for default scenarios
          upsertMessage({
            id: generateSimpleId(),
            role: 'user',
            text: rawUserQuery,
            timestamp: Date.now(),
            session_id: undefined,
          });

          setMessages(prev => [
            ...prev,
            {
              id: `typing-placeholder-${Date.now()}`,
              role: 'ai',
              text: null,
              timestamp: Date.now(),
              isTypingPlaceholder: true,
              session_id: undefined,
            },
          ]);
          setIsTyping(true);
        }
      } else {
        console.log(
          `Resuming session ${currentSessionId} with ${messagesRef.current.length} existing messages - skipping pre-seeding`
        );
      }

      // Load history for existing sessions (only if not already loaded)
      if (currentSessionId && messagesRef.current.length === 0 && !historyLoadedRef.current) {
        loadHistoryOnce(currentSessionId, currentToken);
      }
    };

    ws.current.onclose = ev => {
      setIsConnected(false);
      setIsTyping(false);
      setMessages(prev => prev.filter(m => !m.isTypingPlaceholder));
      isConnectingOrConnected.current = false;

      // Mark connection as failed for non-clean closures or auth failures
      if (ev.code === 4001) {
        setError('Authentication failed.');
        wsConnectionFailed.current = true;
      } else if (ev.code >= 4000 && ev.code < 5000) {
        // Application-level errors (4xxx codes)
        setError('Connection failed due to server error.');
        wsConnectionFailed.current = true;
      } else if (!ev.wasClean && ev.code !== 1000) {
        // Mark as failed to prevent reconnection loops
        // CRITICAL: Don't retry when there's a userQuery - show error instead
        setError('Connection lost unexpectedly.');
        wsConnectionFailed.current = true;
      }
    };

    ws.current.onerror = () => {
      if (!error && !isConnected) setError('WebSocket connection error occurred.');
      setIsConnected(false);
      setIsTyping(false);
      setMessages(prev => prev.filter(m => !m.isTypingPlaceholder));
      isConnectingOrConnected.current = false;
      wsConnectionFailed.current = true; // Mark connection as failed
    };

    ws.current.onmessage = ev => {
      try {
        const msg: WebSocketMessage = JSON.parse(ev.data);
        const ts = msg.timestamp ?? Date.now();

        // Handle error messages from backend
        if (msg.type === 'error') {
          const errorMsg = msg.message || 'An error occurred';
          setError(errorMsg);
          wsConnectionFailed.current = true; // Mark connection as failed to prevent retries
          setIsTyping(false);
          setMessages(prev => prev.filter(m => !m.isTypingPlaceholder));
          return;
        }

        // Handle streaming messages
        if (msg.type === 'ai_chunk') {
          setIsTyping(false);
          handleStreamingMessage(msg);
          return;
        }

        if (msg.type === 'ai') {
          setIsTyping(false);
          setMessages(prev => prev.filter(m => !m.isTypingPlaceholder));
        }

        // Process conversation history
        if (msg.type === 'conversation_history') {
          const _wsCount = msg.messages?.length || 0;
          const currentCount = messagesRef.current.length;

          if (
            currentCount === 0 ||
            !messagesRef.current.some(m => m.session_id === msg.session_id)
          ) {
            handleConversationHistory(
              msg as {
                messages?: Array<{
                  id?: string;
                  type: string;
                  message: string;
                  timestamp?: number;
                  sources?: MessageSource[];
                  feedback?: MessageFeedback;
                }>;
                session_id: string;
              }
            );
          } else {
            // console.log(`History for session ${msg.session_id} already loaded or messages present, skipping.`); // Removed empty block
          }
          return;
        }

        // Skip other control messages that don't have message content
        if (!msg.message && msg.type !== 'system') {
          return;
        }

        // === MODIFIED: Handle session ID updates for ALL message types with session_id ===
        if (msg.session_id) {
          handleSessionIdUpdate(msg.session_id);
        }

        // === System message (welcome etc.) ===
        if (msg.type === 'system' && msg.session_id) {
          // Skip welcome/system intro if we're coming in with a prefilled userQuery or dashboard
          if (rawUserQuery || rawSource === ChatSource.DASHBOARD) {
            // Session ID already handled above, just return
            return;
          }

          const currentSessionFromUrl = sessionIdRef.current;
          const currentMessages = messagesRef.current;

          // Keep short system message if no userQuery and first load
          if (
            (!currentSessionFromUrl || currentMessages.length === 0) &&
            (msg.message?.length || 0) < 500
          ) {
            upsertMessage({
              id: msg.id || generateSimpleId(),
              role: 'system',
              text: msg.message || '',
              timestamp: ts,
              session_id: msg.session_id,
            });
          }

          return;
        }

        // === Echo filtering for default scenarios only ===
        if (
          msg.type === 'user' &&
          rawUserQuery &&
          (!rawSource || rawSource === ChatSource.DEFAULT) &&
          msg.message?.trim() === rawUserQuery.trim()
        ) {
          return;
        }

        // === Skip echo for mood journal user messages ===
        // Check if this user message already exists to avoid duplicates
        if (msg.type === 'user' && rawSource === ChatSource.MOOD_JOURNAL) {
          const existingMsg = messagesRef.current.find(
            m => m.role === 'user' && m.text?.trim() === msg.message?.trim()
          );

          if (existingMsg) {
            // This is an echo of a message we already added in sendMessage, skip it
            return;
          }
          // If message doesn't exist, add it and then add typing indicator
          const newMsg: ChatMessage = {
            id: msg.id || generateSimpleId(),
            role: msg.type as ChatMessage['role'],
            text: msg.message || '',
            sources: msg.sources || [],
            feedback: msg.feedback,
            timestamp: ts,
            session_id: msg.session_id || currentWsSessionIdRef.current || undefined,
          };

          upsertMessage(newMsg);

          // Add typing indicator after the user message
          setMessages(prev => {
            // Only add if there isn't already a typing placeholder
            const hasTypingPlaceholder = prev.some(m => m.isTypingPlaceholder);
            if (!hasTypingPlaceholder) {
              return [
                ...prev,
                {
                  id: `typing-placeholder-${Date.now()}`,
                  role: 'ai',
                  text: null,
                  timestamp: Date.now(),
                  isTypingPlaceholder: true,
                  session_id: newMsg.session_id,
                },
              ];
            }
            return prev;
          });
          setIsTyping(true);
          return;
        }

        // === Normal message handling ===
        const newMsg: ChatMessage = {
          id: msg.id || generateSimpleId(),
          role: msg.type as ChatMessage['role'],
          text: msg.message || '',
          sources: msg.sources || [],
          feedback: msg.feedback,
          timestamp: ts,
          session_id: msg.session_id || currentWsSessionIdRef.current || undefined,
        };

        upsertMessage(newMsg);
      } catch (_error: unknown) {
        const errorMessage = _error instanceof Error ? _error.message : 'Unknown error';
        console.error('Failed to parse WebSocket message:', errorMessage);
      }
    };
  }, [
    handleConversationHistory,
    updateUrlWithSessionId,
    onSessionCreated,
    loadHistoryOnce,
    rawLocale,
    rawUserQuery,
    upsertMessage,
    rawQuestion, // NEW: Add to dependencies
    rawSource, // Added missing dependency
    isConnected, // Added missing dependency
    error, // Added missing dependency
    setIsTyping, // Added missing dependency
    setMessages, // Added missing dependency
  ]);

  // effect: connect / cleanup
  useEffect(() => {
    if (justUpdatedUrl.current) {
      justUpdatedUrl.current = false;
      return;
    }

    // Prevent reconnection if previous connection failed
    if (wsConnectionFailed.current && connectionAttempts.current > 0) {
      console.log('Skipping reconnection - previous connection failed');
      return;
    }

    if (token) {
      connectWebSocket();
    } else {
      if (ws.current) {
        ws.current.close(1000, 'No token');
        ws.current = null;
        isConnectingOrConnected.current = false;
      }
    }

    return () => {
      if (ws.current) {
        ws.current.onopen = ws.current.onmessage = ws.current.onerror = ws.current.onclose = null;
        try {
          if (
            ws.current.readyState === WebSocket.OPEN ||
            ws.current.readyState === WebSocket.CONNECTING
          ) {
            ws.current.close(1000, 'Cleanup');
          }
        } catch {
          // Ignore errors during cleanup
        }
        ws.current = null;
      }
      isConnectingOrConnected.current = false;
    };
  }, [token, sessionIdFromUrl, locale, connectWebSocket, rawQuestion]);

  // send message
  const sendMessage = useCallback(
    (text: string) => {
      if (!isConnected || ws.current?.readyState !== WebSocket.OPEN) {
        setError('Cannot send message: Not connected.');
        return;
      }
      const trimmed = text.trim();
      if (!trimmed) return;
      const id = generateSimpleId();
      const currentSessionId = currentWsSessionIdRef.current;

      // Add user message to UI immediately
      upsertMessage({
        id,
        role: 'user',
        text: trimmed,
        timestamp: Date.now(),
        session_id: currentSessionId,
      });

      // Add a typing placeholder message immediately
      setMessages(prev => {
        // Only add if there isn't already a streaming AI message or a typing placeholder
        const hasActiveAiResponse = prev.some(
          m => m.role === 'ai' && (m.isStreaming || m.isTypingPlaceholder)
        );
        if (!hasActiveAiResponse) {
          return [
            ...prev,
            {
              id: `typing-placeholder-${Date.now()}`,
              role: 'ai',
              text: null, // No text for a pure typing indicator
              timestamp: Date.now(),
              isTypingPlaceholder: true, // Mark it as a placeholder
              session_id: currentSessionId,
            },
          ];
        }
        return prev; // Don't add if already actively typing/streaming
      });

      setIsTyping(true); // This tells the ChatScreen that the AI is processing

      try {
        ws.current!.send(
          JSON.stringify({
            message: trimmed,
            message_id: id,
          })
        );
      } catch {
        // Ignore send errors, error state is handled by onclose/onerror
        setIsTyping(false); // Also turn off on send error
        // Remove the typing placeholder if sending failed
        setMessages(prev => prev.filter(m => !m.isTypingPlaceholder));
      }
    },
    [isConnected, upsertMessage, setIsTyping, setMessages]
  ); // Ensure setIsTyping is in dependency array

  return {
    messages,
    isConnected,
    error,
    sendMessage,
    isLoadingHistory,
    currentSessionId: currentWsSessionId,
    locale,
    isTyping,
  };
};
